"use client";

import { useEffect, useState, useCallback } from "react";
import { cn } from "@/lib/utils";

export type DotLoaderProps = {
  frames: number[][];
  onComplete?: () => void;
  className?: string;
  dotClassName?: string;
  repeatCount?: number;
  duration?: number;
};

export const DotLoader = ({
  frames,
  onComplete,
  className,
  dotClassName,
  repeatCount = 1,
  duration = 150,
}: DotLoaderProps) => {
  const [frameIndex, setFrameIndex] = useState(0);
  const [repeatsDone, setRepeatsDone] = useState(0);

  const currentFrame = frames[frameIndex] || [];
  const gridSize = Math.max(...frames.flat()) + 1;

  const advanceFrame = useCallback(() => {
    setFrameIndex((prev) => {
      const next = prev + 1;
      if (next >= frames.length) {
        setRepeatsDone((r) => r + 1);
        return 0;
      }
      return next;
    });
  }, [frames.length]);

  useEffect(() => {
    if (repeatsDone >= repeatCount) {
      onComplete?.();
      setRepeatsDone(0);
      return;
    }

    const timer = setTimeout(advanceFrame, duration);
    return () => clearTimeout(timer);
  }, [frameIndex, repeatsDone, repeatCount, duration, advanceFrame, onComplete]);

  return (
    <div
      className={cn("grid grid-cols-3 gap-0.5", className)}
      style={{ gridTemplateColumns: `repeat(${Math.ceil(Math.sqrt(gridSize))}, 1fr)` }}
    >
      {Array.from({ length: gridSize }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "w-1.5 h-1.5 rounded-full transition-all duration-100",
            currentFrame.includes(i) ? "active" : "",
            dotClassName
          )}
        />
      ))}
    </div>
  );
};
